// Used in resource.component.html, assignable-preview-details.component.html, preview-asset-dialog.component.html,
// assignment-overview-page.component.html, assignable-preview-container.component.html, reports-assignment-overview-modal.component.html

// Angular
import {
    animate,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import {
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";

// Services/Types/Helpers
import { SnackBarService } from "#core/services";
import { AssetAssociationsHelper } from "#helpers/asset-associations.helper";
import { AssignmentHelper } from "#helpers/assignment.helper";
import { GetDiscoverActivityTypeNameHelper } from "#helpers/get-discover-activity-type.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { PacingHelper } from "#helpers/pacing.helper";
import { LocalStorage } from "#services/local-storage.service";
import {
    AssetTypeHelper,
    PPUser as User
} from "cli-types";

// Components
import { ActivityPreviewDialogComponent } from "#components/dialog-boxes/activity-preview-dialog/activity-preview-dialog.component";

// RXJS/Store
import * as fromCourseStore from "#core/teacher-classes/store";
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as studentsStore from "#student/student-shared/store";
import { EditionId } from "@carnegie-learning/common-types";
import { Asset, Bundle, CogTutorWorkspace, CustomActivity, Edition, ExtLink, Perform, Subject as SubjectType, assetActivityTypeIds, assetTypeIds, eBookActivity, eReader } from "@carnegie-learning/cure-types";
import { AssetFilesService } from "@carnegie-learning/data-access-cure";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ASSET_FILES_SERVICE } from "@carnegie-learning/data-access-angular-cure";

interface ExpansionAsset extends Asset {
    expanded?: boolean;
}

@Component({
    selector: "assignable-preview-details",
    templateUrl: "./assignable-preview-details.component.html",
    styleUrls: ["./assignable-preview-details.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger("detailExpand", [
            state("void", style({ height: "0px", minHeight: "0", visibility: "hidden" })),
            state("*", style({ height: "*", visibility: "visible" })),
            transition("void <=> *", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
        ]),
    ],
    standalone: false
})
export class AssignablePreviewDetailsComponent
    implements OnInit, OnDestroy, OnChanges
{
    private ngUnsubscribe = new Subject();
    activityPreviewDialogRef: MatDialogRef<ActivityPreviewDialogComponent> | null;
    listView: boolean;
    isMath: boolean;
    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;
    @Input() asset?: Asset;
    @Input() embedded: boolean;
    @Input() selectedSubTask: Asset;
    @Input() showActivityName: boolean = true;
    @Input() hideLaunchButton: boolean;
    @Input() hideSubActivities: boolean;
    @Input() compositeTasksLength: number;
    @Input() isTeacher = true;
    @Input() creatingLesson: boolean;
    @Input() assigned: boolean;
    @Input() selectedBundle?: Asset;
    @Input() showAssignButton?: boolean;
    @Input() noBorder = false;
    @Input() hideProgramLogo = false;

    @Output() handleAssignClickFn = new EventEmitter<any>();

    @ViewChild(MatSort) sort: MatSort;

    userData: User;

    private assetFileService = inject<AssetFilesService>(ASSET_FILES_SERVICE);
    dashes = "---";
    levelDisplay: string;
    levelLabel: string;
    compositeActivities: Asset[];
    tableDataSource: MatTableDataSource<Asset>;
    displayedColumns: string[];
    activityType: string;
    pacingIconPath = "./assets/images/icons/timeIcon.svg";
    clLogoPath = "./assets/images/icons/CLLogo.svg";
    teacherAuthoredPath = "./assets/images/icons/teacher-authored-icon.svg";
    subject: SubjectType;
    subjects: SubjectType[];
    editionId: EditionId = "";
    editions: Edition[];
    eBookActivityAsset: eBookActivity;
    extLinkAsset: ExtLink;
    linkedExtLinks: Asset[] = [];
    workspaceOptions: string[] = [];
    programSVG = "CL-bug-blank";
    spineImage$: Observable<string> | null = null;
    isSpineImageLoaded: boolean = false;
    activitiesSectionLabel = {
        name: "Activities Included",
        fontSize: "14px",
    };

    constructor(
        private assignmentHelper: AssignmentHelper,
        private assetTypeHelper: AssetTypeHelper,
        private languageHelper: LanguageHelper,
        public dialog: MatDialog,
        private titleService: Title,
        private router: Router,
        private route: ActivatedRoute,
        private localStorage: LocalStorage,
        private getDiscoverActivityTypeNameHelper: GetDiscoverActivityTypeNameHelper,
        private pacingHelper: PacingHelper,
        private assetAssociationHelper: AssetAssociationsHelper,
        private courseStore: Store<fromCourseStore.CoursesState>,
        private studentCourseStore: Store<studentsStore.StudentCoursesState>,
        private snackBar: SnackBarService
    ) {
        this.userData = this.localStorage.get("userProfile");
    }

    ngOnInit() {
        this.listView = true;
        this.titleService.setTitle("Carnegie Learning - Assignment Preview");

        if (!this.isTeacher) {
            this.studentCourseStore
                .pipe(select(studentsStore.getAllActiveSubjects))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((subjects: SubjectType[]) => {
                    if (subjects && subjects.length > 0) {
                        const subject = subjects.find(
                            (s) => s.subjectId === this.asset?.subjectId
                        );
                        this.subjects = subjects;
                        if (subject) {
                            this.subject = subject;
                        }
                        this.setActivityData();
                    }
                });
            this.studentCourseStore
                .pipe(select(studentsStore.getAllActiveEditions))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((editions: Edition[]) => {
                    if (editions && editions.length) {
                        this.editions = editions;
                    }
                });
            this.studentCourseStore
                .pipe(select(studentsStore.getSelectedCourse))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((course) => {
                    if (course) {
                        this.editionId = course.editionId;
                    }
                });
        } else {
            this.courseStore
                .pipe(select(fromCoursesStore.getAllActiveSubjects))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((subjects: SubjectType[]) => {
                    if (subjects && subjects.length > 0) {
                        const subject = subjects.find(
                            (s) => s.subjectId === this.asset?.subjectId
                        );
                        this.subjects = subjects;
                        if (subject) {
                            this.subject = subject;
                        }
                        this.setActivityData();
                    }
                });
            this.courseStore
                .pipe(select(fromCoursesStore.getAllActiveEditions))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((editions: Edition[]) => {
                    if (editions && editions.length) {
                        this.editions = editions;
                    }
                });
            // get selected asset editionId from URL
            this.route.params
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((urlParams) => {
                    if (urlParams && urlParams["editionId"]) {
                        this.editionId = urlParams["editionId"].toUpperCase();
                        const edition = this.editions.find(
                            (e) => e.editionId === this.editionId
                        );
                        if (edition) {
                            this.programSVG =
                                this.languageHelper.getSubjectLogo(
                                    edition.subjectId,
                                    <string>this.editionId
                                );
                        }
                    }
                });

            this.courseStore
                .pipe(select(fromCoursesStore.getCourseByRoute))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((course) => {
                    if (course) {
                        this.editionId = course.editionId;
                        const edition = this.editions.find(
                            (e) => e.editionId === this.editionId
                        );
                        if (edition) {
                            this.programSVG =
                                this.languageHelper.getSubjectLogo(
                                    edition.subjectId,
                                    <string>this.editionId
                                );
                        }
                    }
                });

            this.isMath = this.languageHelper.ifMathLanguage(
                this.asset?.subjectId
            );
        }
    }

    ngOnChanges() {
        this.setActivityData();
    }

    setActivityData() {
        if (this.asset) {
            this.eBookActivityAsset = <eBookActivity>this.asset;
            this.extLinkAsset = <ExtLink>this.asset;
            const currentAsset = this.asset;
            let subject: SubjectType | undefined;
            this.isMath = this.asset.subjectId === "MATH";
            if (this.hasFormatOptions(this.asset) && this.asset.linkedAssets) {
                this.linkedExtLinks = this.asset.linkedAssets.filter((asset) =>
                    this.assignmentHelper.hasLaunchLink(asset)
                );
                const workspaceOpts: string[] = [this.getLocale(this.asset)],
                    uniqueLangs = new Set(
                        workspaceOpts.concat(
                            this.asset.linkedAssets.map((ast) =>
                                this.getLocale(ast)
                            )
                        )
                    );
                this.workspaceOptions = [];
                uniqueLangs.forEach((ind) => {
                    this.workspaceOptions.push(ind);
                });
            }
            if (!this.subject && this.subjects) {
                subject = this.subjects.find(
                    (s) => s.subjectId === this.asset?.subjectId
                );
                if (subject) {
                    this.subject = subject;
                }
            }
            let tempBundle: Bundle | null;
            if (
                this.asset.assetTypeId === assetTypeIds.BUNDLE &&
                (<Bundle>this.asset).tasks
            ) {
                tempBundle = <Bundle>this.asset;
                this.compositeActivities = tempBundle.tasks; // I can't imagine you'd ever have a bundle without tasks, right? That would be unintelligible
            }
            this.levelLabel = "Level";
            this.levelDisplay = this.assetAssociationHelper
                .getLevels(this.subject)
                .map((l) => l.levelName)
                .join(", ");

            if (this.compositeActivities) {
                this.displayedColumns = this.languageHelper.ifMathLanguage(
                    this.asset.subjectId
                )
                    ? ["type", "name", "level", "pacing"]
                    : ["type", "name", "level"];
                // Make sure these have values editionIds, subjectId, and levelIds. They won't coming from an IPA, so add the asset's values.
                // Was getting assigning new values to readonly object when just using this.compositeActivities in the forEach
                const updatedCompositeActivities: Asset[] = JSON.parse(
                    JSON.stringify(this.compositeActivities)
                );
                updatedCompositeActivities.forEach((a, index) => {
                    a.editionIds = currentAsset.editionIds;
                    a.subjectId = currentAsset.subjectId;
                    a.levelIds = currentAsset.levelIds;
                });
                this.compositeActivities = updatedCompositeActivities;

                this.tableDataSource = new MatTableDataSource(
                    this.compositeActivities
                );
                if (
                    this.tableDataSource &&
                    this.asset?.assetTypeId === assetTypeIds.BUNDLE
                ) {
                    this.tableDataSource.sort = this.sort;
                    this.tableDataSource.sortingDataAccessor = (
                        item,
                        property: string
                    ) => {
                        switch (property) {
                            case "name":
                                return item.displayName;
                            case "type":
                                return this.getTypeName(item);
                            case "level":
                                return this.assetAssociationHelper.getLevels(
                                    this.subject
                                );
                            case "pacing":
                                return this.pacingHelper.getMaxPacing(
                                    this.pacingHelper.getAssetPacing(
                                        <Bundle>item
                                    )
                                );
                            default:
                                return (<any>item)[property];
                        }
                    };
                }
            }

            if (this.asset.assetTypeId === assetTypeIds.EBOOK_ACT) {
                this.activityType = (<eBookActivity>(
                    this.asset
                )).bookActivityType;
            }
        }
    }

    // Get languageID
    getLanguageID(asset: Asset) {
        return asset.subjectId;
    }

    // Get the correct icon name to access it's svg icon
    getIconPath(asset: Asset | undefined): string {
        if (this.selectedSubTask) {
            if (this.selectedSubTask.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    this.selectedSubTask.assetActivityTypeId
                );
            }
            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                this.selectedSubTask.assetTypeId
            );
        } else if (asset) {
            let assetType: string = asset.assetTypeId;
            if (asset.assetActivityTypeId) {
                assetType = asset.assetActivityTypeId;
            }
            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                assetType
            );
        }

        return "";
    }

    // Format shortAssignableType for display
    getTypeName(asset: Asset | undefined) {
        if (this.selectedSubTask) {
            if (this.selectedSubTask.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    this.selectedSubTask.assetActivityTypeId
                );
            }
            return this.assetTypeHelper.getCapTypeName(
                this.selectedSubTask.assetTypeId
            );
        } else if (asset) {
            if (asset.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    asset.assetActivityTypeId
                );
            }
            return this.assetTypeHelper.getCapTypeName(asset.assetTypeId);
        }
    }

    getDisplayName(asset: Asset | undefined): string {
        let name = "";
        if (asset) {
            name = this.assignmentHelper.getAssetDisplayName(asset);
        }
        return name;
    }

    launchWsByLanguage(lang: string): void {
        const langAbbr = this.languageHelper.getLocaleAbbr(lang);
        let asset: Asset | undefined;
        if (this.asset?.locales.map((l) => l.slice(0, 2)).includes(langAbbr)) {
            asset = this.asset;
        } else if (this.asset?.linkedAssets) {
            asset = this.asset?.linkedAssets.find((la) =>
                la.locales.map((l) => l.slice(0, 2)).includes(langAbbr)
            );
        }
        this.launchWorkspace(asset);
    }

    // Launch the dialog box that shows the Workspace iframe
    launchWorkspace(langOpt?: Asset) {
        this.courseStore
            .pipe(
                select(fromCourseStore.getCoursesEntities),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((courses) => {
                let launch = false;
                const coursesArray = Array.from(courses.values());

                if (this.asset) {
                    const assetToLaunch: Asset = langOpt ? langOpt : this.asset;
                    launch = coursesArray.some(
                        (course) => course.subjectId === assetToLaunch.subjectId
                    );
                    if (
                        !launch &&
                        [assetTypeIds.EREADER, assetTypeIds.EBOOK_ACT].includes(
                            assetToLaunch.assetTypeId
                        )
                    ) {
                        this.snackBar.error(
                            `Please create a class with program:
                            ${this.getProgramName()} selected in order to view this activity`
                        );
                        return;
                    }
                    this.activityPreviewDialogRef = this.dialog.open(
                        ActivityPreviewDialogComponent,
                        {
                            data: {
                                asset: assetToLaunch,
                                compositeID: null,
                                compositeType: null,
                                userID: this.userData.userID,
                            },
                        }
                    );
                }
            });
    }

    launchEtext() {
        // console.log("Launch the eText. Unsure how this will work or when they will show up.");//TODO: Something, as yet not known.
    }

    launchLink(asset: Asset | undefined) {
        if (this.hasLaunchLink(asset)) {
            // this logic may need to get smarter...
            window.open((<ExtLink>asset).launchLink);
        }
    }

    getLocale(asset: Asset | undefined): string {
        let str = "";
        if (asset?.locales.length) {
            str = asset?.locales[0].toString() || "";
        }
        return this.languageHelper.getLocale(str);
    }

    getLinkText(asset: Asset | undefined): string {
        return this.assetTypeHelper.getCapTypeName(asset?.assetActivityTypeId); // (asset?.assetActivityTypeId === assetActivityTypeIds.SLIDES) ? "Google Slides" : "Enhanced eText";
    }

    hasLaunchLink(asset: Asset | undefined): boolean {
        return this.assignmentHelper.hasLaunchLink(asset);
    }

    getFileType(asset: Asset | undefined): string {
        return this.assignmentHelper.getFileType(asset);
    }

    hasFormatOptions(asset: Asset | undefined): boolean {
        return this.hasLaunchLink(asset) || !!asset?.linkedAssets?.length;
    }

    optionsHeaderText(asset: Asset | undefined): string {
        let header = "Language Options";
        if (this.hasLaunchLink(asset)) {
            header = "Format Options";
        }
        return header;
    }

    showLearningPoints(asset: Asset | undefined): boolean {
        return (
            !!asset &&
            ![
                assetTypeIds.CUSTOM_ACT,
                assetTypeIds.PERFORM,
                assetTypeIds.READYCHECK,
            ].includes(asset.assetTypeId)
        );
    }

    getLaunchTitle() {
        if (this.asset?.assetTypeId === assetTypeIds.READYCHECK) {
            return "Assessment";
        } else if (
            this.isMath &&
            this.asset?.assetTypeId === assetTypeIds.COG_TUTOR
        ) {
            return "Workspace";
        }

        return "Activity";
    }

    getDescription() {
        if (this.selectedSubTask) {
            return this.selectedSubTask.description;
        } else if (this.asset?.description) {
            return this.asset.description;
        } else {
            return "No Description Available";
        }
    }

    // Convert seconds into hh:mm:ss
    getPacing(asset: Asset | undefined) {
        if (asset?.assetTypeId === assetTypeIds.BUNDLE) {
            return this.pacingHelper.getAssetPacing(<Bundle>asset);
        }

        if (
            asset &&
            [
                assetTypeIds.COG_TUTOR,
                assetTypeIds.READYCHECK,
                assetTypeIds.CUSTOM_ACT,
            ].includes(asset.assetTypeId)
        ) {
            return this.pacingHelper.getAssetPacing(<CogTutorWorkspace>asset);
        }

        return this.dashes;
    }

    getPointsPossible() {
        if (this.selectedSubTask) {
            return this.assignmentHelper.getAssetProgressPossible(
                <CogTutorWorkspace>this.selectedSubTask
            );
        } else if (this.asset?.assetTypeId === assetTypeIds.BUNDLE) {
            return this.assignmentHelper.getAssetProgressPossible(
                <Bundle>this.asset
            );
        } else {
            return this.assignmentHelper.getAssetProgressPossible(
                <CogTutorWorkspace>this.asset
            );
        }
    }

    // get Grade display
    getGradeDisplay(subActivity: any): string {
        //CompositeTaskCategory
        let level = "";
        if (this.asset?.assetTypeId === assetTypeIds.BUNDLE) {
            level = this.levelDisplay;
        } else {
            level = subActivity.levelDisplay ? subActivity.levelDisplay : "N/A";
            level =
                subActivity.shortAssignableType === assetTypeIds.EBOOK_ACT &&
                level.toString().length < 3
                    ? subActivity.levelLabel + " " + level.toString()
                    : level; /* PAS-7168 */
        }
        return level;
    }

    getLevels(asset: Asset | undefined): string {
        if (
            asset &&
            (this.editionId || asset?.subjectId) &&
            this.subjects &&
            this.editions
        ) {
            const subject = this.subjects.find(
                (d) => d.subjectId === asset.subjectId
            );
            const edition = this.editions.find(
                (d) => d.editionId === this.editionId
            );
            if (subject || edition) {
                return this.assetAssociationHelper.getAssetLevelNames(
                    asset,
                    subject,
                    edition,
                    this.editions
                );
            }
        }
        return this.dashes;
    }

    getLevelLabel(asset: Asset | undefined): string {
        let label =
            asset?.subjectId === "LANG" &&
            [
                assetTypeIds.PERFORM,
                assetTypeIds.CUSTOM_ACT,
                assetTypeIds.EBOOK_ACT,
                assetTypeIds.EREADER,
            ].includes(asset.assetTypeId)
                ? "Grade"
                : "Level";
        if (
            asset?.subjectId === "COMP-SCI" &&
            [
                assetTypeIds.CUSTOM_ACT,
                assetTypeIds.EREADER,
                assetTypeIds.PERFORM,
                assetTypeIds.BUNDLE,
            ].includes(asset.assetTypeId)
        ) {
            label = "Course";
        }
        if (asset) {
            const levelTest = this.getLevels(asset);
            if (levelTest) {
                label = levelTest.indexOf(",") > -1 ? label + "s" : label;
            } else {
                label =
                    asset && asset.levelIds.length > 1 ? label + "s" : label;
            }
        }
        return label;
    }

    getSubActivityResponseType(subActivity: CustomActivity) {
        const responseType = subActivity.responseType;
        if (responseType === "both") {
            return "Url/File Upload and Short Answer";
        } else if (responseType === "file") {
            return "Url/File Upload";
        } else if (responseType === "text") {
            return "Short Answer";
        } else {
            return "No Student Response";
        }
    }

    getAuthor(visibility: string | undefined, publishedBy: string) {
        if (visibility) {
            let ownerVal: string;
            const userFullName =
                this.userData.firstName + " " + this.userData.lastName;

            switch (visibility) {
                case "self":
                    ownerVal = userFullName;
                    break;
                case "district":
                    ownerVal =
                        this.userData.userGUID === publishedBy
                            ? userFullName
                            : "My District";
                    break;
                case "school":
                    ownerVal =
                        this.userData.userGUID === publishedBy
                            ? userFullName
                            : "My School";
                    break;
                case "none":
                    ownerVal =
                        this.userData.userGUID === publishedBy
                            ? userFullName
                            : "Passport Community";
                    break;
                default: // I suspect that values of none, emc, or anything else should just be Carnegie Learning.
                    ownerVal = "Carnegie Learning";
                    break;
            }
            return ownerVal;
        } else if (this.asset) {
            // Carnegie Learning should just be the default
            return "Carnegie Learning";
        }
    }

    isCLLogo(): boolean | undefined {
        return (
            this.asset &&
            this.getAuthor(this.asset?.visibility, this.asset?.publishedBy) ===
                "Carnegie Learning"
        );
    }

    previewSubActivity(subActivity: { [index: string]: string | boolean }) {
        const fromReports = this.router.url.includes("reports");
        if (!fromReports) {
            this.router.navigate([`activity/${subActivity.assetId}`], {
                relativeTo: this.route,
            });
        } else {
            const targetAsset: ExpansionAsset | undefined =
                this.tableDataSource.data.find(
                    (asset) => asset.assetId === subActivity.assetId
                );
            if (targetAsset) {
                targetAsset.expanded = !targetAsset.expanded;
            } else {
                this.router.navigate([
                    "/teacher/assign/" +
                        this.asset?.editionIds[0] +
                        "/preview/" +
                        this.asset?.assetId +
                        "/activity/" +
                        subActivity.assetId,
                ]);
                this.dialog.closeAll();
            }
        }
    }

    getSpineImage() {
        // if (this.asset.assetTypeId === "Module") {
        //     return (
        //         "./assets/images/" +
        //         this.assignable.image +
        //         "." +
        //         this.assignable.imageExt
        //     );
        // } else {
        if (!this.spineImage$) {
            const fileName =
                "images/" +
                (<eBookActivity>this.asset).bookImage +
                "-lg." +
                (<eBookActivity>this.asset).bookImageExt;
            this.spineImage$ = this.assetFileService.getFileUrl(fileName);
        }
        return this.spineImage$;
    }

    getProgramName() {
        if (
            this.asset &&
            this.asset.editionIds &&
            this.asset.editionIds.length &&
            this.editionId &&
            this.asset.subjectId
        ) {
            const assetEdition =
                this.editions && this.editions.length
                    ? this.editions.find(
                          (edt) => this.editionId === edt.editionId
                      )
                    : undefined;

            let editionName: string | undefined;
            if (assetEdition) {
                editionName = assetEdition.editionName;
            }
            return editionName || this.subject?.subjectName;
        } else {
            return this.dashes;
        }
    }

    getDiscoverActivityTypeName(activityType: string) {
        return this.getDiscoverActivityTypeNameHelper.getDiscoverActivityTypeName(
            activityType
        );
    }

    // Get the number of activities for a Lesson or Module
    getActivitiesCount(asset: Asset | undefined) {
        if (asset?.assetTypeId === assetTypeIds.BUNDLE) {
            return (<Bundle>asset).tasks?.length;
        }
    }

    getUnit(asset: Asset | undefined): string {
        let unit: string = this.dashes; // if undefined just use the dashes
        if (asset) {
            switch (asset.assetTypeId) {
                case assetTypeIds.CUSTOM_ACT:
                    unit = (<CustomActivity>asset).unit || unit;
                    break;
                case assetTypeIds.PERFORM:
                    unit = (<Perform>asset).unit || unit;
                    break;
                case assetTypeIds.BUNDLE:
                    unit = (<Bundle>asset).unit || unit;
                    break;
                case assetTypeIds.EBOOK_ACT:
                    unit = (<eBookActivity>asset).unit || unit;
                    break;
                case assetTypeIds.EREADER:
                    unit = (<eReader>asset).unit || unit;
                    break;
            }
        }
        return unit;
    }

    getLesson(asset: Asset | undefined): string {
        let lesson: string = this.dashes; // if undefined just use the dashes
        if (asset) {
            switch (asset.assetTypeId) {
                case assetTypeIds.CUSTOM_ACT:
                    lesson = (<CustomActivity>asset).lesson || lesson;
                    break;
                case assetTypeIds.EREADER:
                    lesson = (<eReader>asset).lesson || lesson;
                    break;
                case assetTypeIds.PERFORM:
                    lesson = (<Perform>asset).lesson || lesson;
                    break;
                case assetTypeIds.EBOOK_ACT:
                    lesson = (<eBookActivity>asset).lesson || lesson;
                    break;
            }
        }
        return lesson;
    }

    getPage(asset: Asset | undefined): string {
        let page: string = this.dashes; // if undefined just use the dashes
        if (asset) {
            switch (asset.assetTypeId) {
                case assetTypeIds.EBOOK_ACT:
                    page = (<eBookActivity>asset).page || page;
                    break;
                case assetTypeIds.CUSTOM_ACT:
                    page = (<CustomActivity>asset).page || page;
                    break;
                case assetTypeIds.PERFORM:
                    page = (<Perform>asset).page || page;
                    break;
            }
        }
        return page;
    }

    getActivityNumber(asset: Asset | undefined): string {
        let activityNumber: string = this.dashes; // if undefined just use the dashes
        if (asset) {
            switch (asset.assetTypeId) {
                case assetTypeIds.CUSTOM_ACT:
                    activityNumber =
                        (<CustomActivity>asset).bookActivityNumber ||
                        activityNumber;
                    break;
                case assetTypeIds.EBOOK_ACT:
                    activityNumber =
                        (<eBookActivity>asset).bookActivityNumber ||
                        activityNumber;
                    break;
            }
        }
        return activityNumber;
    }

    // Get Custom Activity Response Type
    getCustomActivityResponseType(asset: Asset | undefined) {
        if (asset && asset.assetTypeId === assetTypeIds.CUSTOM_ACT) {
            return this.assignmentHelper.formatResponseType(
                (<CustomActivity>asset).responseType
            );
        }
    }

    hidePageAndLesson(asset: Asset | undefined, which: string): boolean {
        let verdict = false;
        if (
            asset &&
            ((which === "lesson" && this.getLesson(asset) === this.dashes) ||
                (which === "page" && this.getPage(asset) === this.dashes))
        ) {
            verdict = true;
        }
        return verdict;
    }

    getAssignTooltip(assigned: boolean) {
        if (assigned) {
            return "Remove from cart";
        } else if (!assigned && !this.creatingLesson) {
            return "Add to cart";
        } else if (
            !assigned &&
            this.creatingLesson &&
            this.asset?.assetTypeId === assetTypeIds.BUNDLE
        ) {
            return "Cannot assign Bundles to cart while in Bundle creation mode";
        }
    }

    handleAssignClick() {
        this.assigned = !this.assigned;
        const payload = { assigned: this.assigned };
        this.handleAssignClickFn.emit(payload);
    }

    // Assignable Sub Title
    getAssignableSubTitle(selectedAsset: Asset | undefined) {
        if (selectedAsset && this.selectedSubTask && this.selectedBundle) {
            const bundleTypeName = this.assetTypeHelper.getCapTypeName(
                this.selectedBundle.assetActivityTypeId
            );
            return (
                `<span class='font-weight-300'>Included in the ${bundleTypeName}:</span> ` +
                this.selectedBundle.displayName
            );
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
